// Rider route pop-up for the charter webchat.
//
// The rider types From / To / Stops; each field is a debounced Google Places AUTOCOMPLETE type-ahead
// (locationResolveApi -> agents proxy -> Java /api/locations/suggest), and picking a row expands it via
// /details into a VERIFIED place (coords + city/state + airport flag). Autocomplete keystrokes are free
// inside a session and only the /details call is billed — see useLocationSuggest() for the session-token
// contract. A Google map shows a marker per verified stop with straight lines between them (a
// confirmation view, not a driving route).
//
// On Confirm it calls onConfirm(routeText, structured): `routeText` is a clean verified sentence the
// chat sends as the rider's next turn (so the agent extracts slots from unambiguous text — no more
// "did you mean...?" loop), and `structured` carries the resolved locations + coords.
//
// The left column is ordered as the trip runs — Departure (From) -> Stops along the way -> Arrival
// (To) — so the form mirrors the map markers (1 = from, 2.. = stops, last = to). The geometry array
// stays [from, ...stops, to] regardless.
//
// Layout / exports:
//   - RoutePopup (default) — the modal component. (The former `mode="shuttle"` variant is retired:
//     the recurring-shuttle route ask now mounts RouteFormMulti's `shuttle` variant — the modern
//     single-search PlaceField surface — see CustomerApp.js.)
//   - buildRouteToken(structured) — pure helper that maps { from, to, stops } to the reverse-route
//     token payload CustomerApp appends to the sent message as `[[ROUTE=<compact-json>]]`. This is the
//     web->agents mirror of the agent->web [[ROUTE_FORM]] token: the agents side reads pickup.address
//     -> "pickup_address", destination.address -> "destination_detail", stops[].address -> "stops",
//     all VERIFIED. Field names (pickup/destination/stops; address/city/state/isAirport/iata) are a
//     FROZEN contract — do not rename. The token must be LAST in the sent string and is stripped before
//     the transcript/reply so the JSON never reaches the rider.
//
// Built on the shared Modal primitive (frosted backdrop, Escape/focus-trap/scroll-lock, portal) and
// the Button primitive + navy `primary` theme tokens, so it matches the rest of the app. Needs
// REACT_APP_GOOGLE_MAPS_API_KEY to render the map; without it the fields still verify and the map
// area shows a graceful placeholder.
//
// Last verified: 2026-07-23 (peoria; shuttle mode retired — the shuttle ask now mounts RouteFormMulti's
// shuttle variant)

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/primitives';
import { cx } from '../ui/cx';
import { suggestLocations, fetchPlaceDetails, newSessionToken } from './locationResolveApi';
import { isSpecificEnough, locationStatus } from './routePlace';
import { dedupeStops, duplicateStopIndices, stateInvalid } from './validation';

const DEBOUNCE_MS = 300;

// Matches the primitives' INPUT_BASE so the fields read as first-class app inputs (Peoria dark theme).
const INPUT_BASE =
  'w-full rounded-btn border bg-peoria-surface2 text-sm text-peoria-text placeholder:text-peoria-muted px-3 py-2 ' +
  'transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-peoria-red/30';

// A single verified location as we keep it in state. Exported so the multi-leg City picker maps the
// same ORS candidate shape.
export function toPlace(candidate) {
  if (!candidate) return null;
  const lat = candidate.latitude != null ? Number(candidate.latitude) : null;
  const lng = candidate.longitude != null ? Number(candidate.longitude) : null;
  return {
    label: candidate.formattedAddress || candidate.name || '',
    // The venue's own name ("Bradley University") kept ALONGSIDE the street address, so one pick
    // supplies both tiers the endpoint used to ask for separately. Callers that already preserve a
    // rider-typed name (RouteFormMulti's onSelectAddr) keep winning — this only fills the fallback.
    name: candidate.name || '',
    city: candidate.city || '',
    state: candidate.state || '',
    lat,
    lng,
    isAirport: !!candidate.isAirport,
    iataCode: candidate.iataCode || null,
    precision: candidate.precision || null,   // Pelias layer: address/venue/street vs locality/region/coarse
    hasCoords: Number.isFinite(lat) && Number.isFinite(lng),
  };
}

function cityState(place) {
  return [place?.city, place?.state].filter(Boolean).join(', ');
}

// Do two place names refer to the same thing? Case/punctuation-insensitive, so "St. Mary's Church"
// matches "St Marys Church". Used to decide whether a suggestion is confidently the place the rider
// named — see the prefill note in PlaceField.
function sameName(a, b) {
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const x = norm(a);
  return !!x && x === norm(b);
}

// Does a geocoded place agree with what the chat captured? Guards the auto-fill: selecting REPLACES
// the whole place, so a mismatch would silently overwrite the city/state the rider stated — the
// planner then prices from a pickup they never named.
//
// Both city and state are checked. State alone isn't enough: "Peoria Riverfront Museum" seeded as
// East Peoria resolves to Peoria — same state, different municipality about five miles off. A field
// MISSING on either side is not a disagreement (the chat often has no city yet), so it doesn't block.
// Exported for the guard tests.
export function agreesWithSeed(resolved, seed) {
  const eq = (a, b) => {
    const x = String(a || '').trim().toLowerCase();
    const y = String(b || '').trim().toLowerCase();
    return !x || !y || x === y;      // absent on either side => no objection
  };
  return !!resolved && eq(resolved.state, seed?.state) && eq(resolved.city, seed?.city);
}

// A verified place rendered for the chat message the agent re-extracts slots from. Bare
// comma-separated city/state (matching the agent's full-address splitting), de-duped so a label that
// already contains the city/state isn't doubled. Exported for reuse by the multi-leg route form.
export function placePhrase(place) {
  const inLabel = (part) =>
    new RegExp(`(^|[,\\s])${part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([,\\s]|$)`, 'i').test(place.label);
  const extras = [place.city, place.state].filter(Boolean).filter((p) => !inLabel(p));
  return extras.length ? `${place.label}, ${extras.join(', ')}` : place.label;
}

// Maps a single verified `structured` place (label/city/state/isAirport/iataCode from toPlace) to the
// reverse-route token's place shape. Field names are the frozen web->agents contract — do NOT rename.
// Exported so the multi-leg route form (RouteFormMulti) reuses the SAME token place mapping.
export function tokenPlace(place, { keepCoarseLabel = false } = {}) {
  // A COARSE pick (a bare city/region) carries a label like "Peoria, IL, USA", but that is NOT a street
  // address — emitting it as one would land in the agents' `pickup_address` slot as a VERIFIED exact
  // address and price off a city centroid dressed up as a pin. For an ENDPOINT we blank it and let the
  // city/state fields carry the meaning, which is exactly the "city is enough for a preliminary
  // estimate" path. Matters more now that one field accepts both a venue and a city.
  //
  // STOPS opt out via keepCoarseLabel. A stop's identity lives ENTIRELY in its address: the agents read
  // `stops[].address` and DROP entries whose address is blank (_route_verified_slots), so blanking a
  // city-level stop ("Dixon") would silently delete it from the itinerary.
  const specific = keepCoarseLabel || isSpecificEnough(place);
  const out = {
    address: (specific && place.label) ? place.label : '',
    city: place.city || '',
    state: place.state || '',
    isAirport: !!place.isAirport,
    iata: place.iataCode || null,
  };
  if (place.name) out.name = place.name;   // venue name — carried back to the agents
  return out;
}

// Builds the reverse-route token payload the chat appends to the confirmed-route message it sends the
// agent: `[[ROUTE=<compact-json>]]`. Mirrors the agent->web [[ROUTE_FORM]] token in the other direction.
// The agents side reads pickup.address -> slot "pickup_address", destination.address ->
// "destination_detail", stops[].address -> planner "stops", all written VERIFIED (never overwritten by
// later LLM extraction). Pure + exported so it's unit-testable; caller wraps it as
// `[[ROUTE=${JSON.stringify(buildRouteToken(structured))}]]` and keeps it LAST in the sent string.
export function buildRouteToken(structured) {
  const { from, to, stops = [] } = structured || {};
  return {
    pickup: tokenPlace(from || {}),
    destination: tokenPlace(to || {}),
    // keepCoarseLabel: a stop is carried by its address alone, so a city-level stop must keep its label.
    stops: stops.filter(Boolean).map((s) => tokenPlace(s, { keepCoarseLabel: true })),
  };
}

// Normalize `initial` into the popup's own seed shape — the classic callers pass { from, to, stops }
// (form places) straight through. (The shuttle {legs:[…]} branch is retired with the shuttle mode:
// the shuttle ask now seeds RouteFormMulti via routeSeed.mapTokenLegs like every other trip type.)
function seedOf(initial) {
  return initial || {};
}

// Debounced AUTOCOMPLETE type-ahead for address fields, with the same open/loading/outside-click
// mechanics as the old /resolve hook but a different economic shape:
//
//   suggest (per keystroke)  -> FREE inside a session, returns id + label only (no coords)
//   details (on selection)   -> the ONE billed call, returns the full verified place
//
// It therefore owns a SESSION TOKEN: minted lazily on the first keystroke of an editing burst, sent
// on every suggest, sent again on the closing details call, then cleared so the next burst starts a
// fresh session. Losing that pairing doesn't break anything — it just bills each keystroke separately
// (~10x).
//
// Used by PlaceField for every place input (endpoints and stops alike).
export function useLocationSuggest() {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const session = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const runSearch = useCallback((q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (!session.current) session.current = newSessionToken();
    timer.current = setTimeout(async () => {
      setLoading(true);
      const results = await suggestLocations(q, session.current);
      setLoading(false);
      setSuggestions(results);
      setOpen(true);
    }, DEBOUNCE_MS);
  }, []);

  // One-shot lookup for a query the RIDER DIDN'T TYPE — the chat already named this place, so we
  // resolve it on the caller's behalf when the form opens. Deliberately NOT runSearch(): that debounce
  // exists to pace a human's keystrokes, and routing a mount-time lookup through it would both delay
  // the prefill and make a later auto-pick indistinguishable from the rider's own typing.
  //
  // Returns the prediction rows and leaves the dropdown closed — the caller decides whether one
  // confident match should auto-fill or several should be offered. Opens the same session the
  // follow-up resolvePick() closes, so the whole prefill bills as ONE lookup.
  const resolveByName = useCallback(async (query) => {
    const q = (query || '').trim();
    if (q.length < 3) return [];
    if (!session.current) session.current = newSessionToken();
    setLoading(true);
    const rows = await suggestLocations(q, session.current);
    setLoading(false);
    return rows;
  }, []);

  // Expand a picked prediction. Consumes the session token (Google bills this call and closes the
  // session), then clears it so the rider's next edit opens a new one. Returns null when Google
  // can't resolve the pick, letting the caller fall back to the typed text.
  const resolvePick = useCallback(async (placeId) => {
    const token = session.current;
    session.current = null;
    setLoading(true);
    const place = await fetchPlaceDetails(placeId, token);
    setLoading(false);
    return place;
  }, []);

  return { suggestions, setSuggestions, open, setOpen, loading, boxRef,
           runSearch, resolveByName, resolvePick };
}

// Debounced type-ahead field bound to the geocoder. onSelect(place) when a candidate is picked;
// onClear() when the rider edits away a locked selection.
// Exported so the multi-leg route form (RouteFormMulti) reuses the SAME verified geocoder typeahead.
export function LocationField({ label, placeholder, value, onSelect, onClear, invalid = false, hint }) {
  const [text, setText] = useState('');
  const { suggestions, setSuggestions, open, setOpen, loading, boxRef, runSearch, resolvePick } =
    useLocationSuggest();

  // Pre-resolved candidates that ride along in the form seed (Phase 3): when a chat-mentioned place was
  // ambiguous ("Hilton"), the server attached several matches and left the label empty so the field stays
  // unlocked. We surface them as a proactive picker (below) instead of re-asking in chat. Dismissable so a
  // rider who'd rather type isn't stuck with the panel.
  const seedCandidates = Array.isArray(value?.candidates) ? value.candidates : [];
  const [pickerDismissed, setPickerDismissed] = useState(false);
  const status = locationStatus(value);
  const showSeedPicker = status === 'needs_confirmation' && seedCandidates.length > 0 && !pickerDismissed && !text;

  useEffect(() => {
    // Only sync the input to an EXTERNALLY-RESOLVED value (a real place carries a non-empty label). A
    // coarse city/state-only seed has an empty label — syncing it would wipe whatever the rider is
    // typing into the address box on every keystroke (the multi-leg form seeds such coarse places).
    if (value?.label) setText(value.label);
  }, [value]);

  const onChange = (e) => {
    const next = e.target.value;
    setText(next);
    // Editing only "unlocks" a REAL resolved address (non-empty label). A coarse city/state-only seed is
    // not a locked address, so typing over it must NOT re-fire onClear (which would spawn a fresh coarse
    // place and reset the input via the [value] effect — the bug that made this field impossible to type in).
    if (value?.label) onClear();
    runSearch(next);
  };

  // Pick a PREDICTION: it carries no coordinates, so expand it via /details (the one billed call of
  // the burst) before locking the field. Show the row's own text immediately so the input doesn't sit
  // empty during the round-trip. If Google can't resolve it, keep the text as free-entry rather than
  // dropping what the rider chose — same graceful degradation as useAsTyped().
  const pick = async (s) => {
    const shown = s.fullText || s.mainText || '';
    setText(shown);
    setSuggestions([]);
    setOpen(false);
    const resolved = await resolvePick(s.placeId);
    if (resolved) {
      const place = toPlace(resolved);
      setText(place.label || shown);
      onSelect(place);
      return;
    }
    onSelect({ label: shown, city: '', state: '', lat: null, lng: null,
               isAirport: false, iataCode: null, precision: null, hasCoords: false });
  };

  // Pick a pre-resolved SEED candidate (already in the form-place shape from routeSeed) — no re-geocode.
  const pickSeed = (place) => {
    const chosen = { ...place, candidates: undefined };
    setText(chosen.label || '');
    setPickerDismissed(true);
    onSelect(chosen);
  };

  // FREE-TEXT fallback (no geocoder): accept exactly what the rider typed as the address. precision is
  // null so isSpecificEnough fails open → Confirm enables; downstream ([[ROUTE=...]] → _route_verified_slots)
  // already tolerates a bare address with empty city/state. This is the "resolver" without any search API.
  const useAsTyped = () => {
    const t = text.trim();
    if (!t) return;
    setSuggestions([]);
    setOpen(false);
    onSelect({ label: t, city: '', state: '', lat: null, lng: null,
               isAirport: false, iataCode: null, precision: null, hasCoords: false });
  };

  // "Verified" = a REAL resolved place is locked in (non-empty label). A coarse city/state-only seed is
  // NOT verified — so it shows no green ✓ and no locked styling over an empty address field.
  const verified = !!(value && value.label);
  const tooCoarse = verified && invalid;   // picked, but not specific enough (a bare city/area)
  // 4-state status drives the field's color/glyph/hint so it communicates state instead of just pass/fail.
  const isResolved = status === 'resolved' && !invalid;
  const isApprox = status === 'approximate' || tooCoarse;
  const isUnresolved = status === 'unresolved' && verified;

  return (
    <label className="block space-y-1.5" ref={boxRef}>
      <div className="text-sm font-medium text-peoria-text">{label}</div>
      <div className="relative">
        <input
          className={cx(
            INPUT_BASE,
            isApprox
              ? 'border-amber-500/60 bg-amber-500/10'
              : isResolved
              ? 'border-green-500/60 bg-green-500/10'
              : showSeedPicker
              ? 'border-peoria-red/50'
              : 'border-peoria-border focus:border-peoria-red'
          )}
          placeholder={placeholder}
          value={text}
          onChange={onChange}
          onFocus={() => suggestions.length && setOpen(true)}
        />
        {isResolved && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-700 text-sm">✓</span>}
        {isApprox && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-700 text-sm font-semibold">≈</span>}
        {loading && !verified && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-peoria-muted text-11">…</span>
        )}
        {showSeedPicker && (
          <div className="absolute z-10 mt-1 w-full rounded-card border border-peoria-red/40 bg-peoria-surface2 shadow-pop">
            <div className="flex items-center justify-between px-3 pt-2 pb-1">
              <span className="text-11 font-medium text-peoria-muted">We found a few possible matches — is this the one you meant?</span>
              <button type="button" onClick={() => setPickerDismissed(true)}
                className="text-11 text-peoria-muted hover:text-peoria-text">type instead</button>
            </div>
            <ul className="max-h-56 overflow-auto">
              {seedCandidates.map((p, i) => (
                <li key={p.placeId || `${p.label}-${i}`}>
                  <button
                    type="button"
                    className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-peoria-surface focus:bg-peoria-surface focus:outline-none"
                    onClick={() => pickSeed(p)}
                  >
                    <span className="flex items-center gap-1.5 text-13 font-medium text-peoria-text">
                      {p.isAirport && <span className="text-peoria-red">✈</span>}
                      {p.label || cityState(p)}
                    </span>
                    {p.label && cityState(p) && <span className="text-11 text-peoria-muted">{cityState(p)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {open && suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-card border border-peoria-border bg-peoria-surface2 shadow-pop">
            {/* A prediction has no coords/airport flag yet (that arrives with /details on click), so a
                row renders Google's own two-line split: the place, then its disambiguating context. */}
            {suggestions.map((s, i) => (
              <li key={s.placeId || i}>
                <button
                  type="button"
                  className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-peoria-surface focus:bg-peoria-surface focus:outline-none"
                  onClick={() => pick(s)}
                >
                  <span className="text-13 font-medium text-peoria-text">{s.mainText}</span>
                  {s.secondaryText && (
                    <span className="text-11 text-peoria-muted">{s.secondaryText}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && !loading && suggestions.length === 0 && text.trim().length >= 3 && (
          <div className="absolute z-10 mt-1 w-full rounded-card border border-peoria-border bg-peoria-surface2 px-3 py-2 shadow-pop">
            <div className="text-11 text-peoria-muted mb-1">No match from search — you can enter it manually.</div>
            <button type="button" onClick={useAsTyped}
              className="text-13 font-medium text-peoria-red underline hover:opacity-80">
              Use “{text.trim()}” as-is
            </button>
          </div>
        )}
      </div>
      {isApprox ? (
        <div className="text-11 text-amber-700">
          {hint || 'Approximate — we’ll use the city for now and refine this later.'}
        </div>
      ) : isUnresolved ? (
        <div className="text-11 text-peoria-muted">Kept as typed — we couldn’t identify this yet, but you can continue.</div>
      ) : isResolved && cityState(value) ? (
        <div className="text-11 text-peoria-muted">
          {value.isAirport ? '✈ ' : ''}
          {cityState(value)}
          {value.iataCode ? ` · ${value.iataCode}` : ''}
        </div>
      ) : null}
    </label>
  );
}

// ONE field per endpoint: search a place, pick it, done.
//
// Replaces the old four-input endpoint (City + ST + Location name + Exact address). That shape existed
// because ORS/Pelias couldn't resolve a venue NAME — the rider had to supply the city/state by hand and
// the venue name was kept as separate free text. Google Places returns name + street + city + state +
// coords from a single pick, so all four collapse into one search.
//
// The box is ALWAYS a text input — it never swaps to a read-only summary. A resolved place simply
// shows up as the input's value with a green border + ✓, and typing over it unlocks and searches
// again, so "edit this" needs no separate control. Status is driven by the SAME locationStatus() the
// rest of the form uses:
//   (empty)     — placeholder, ready to search
//   resolved ✓  — a real address/venue: green, with the venue name + City, ST beneath
//   approximate ≈ — a bare city (or coarse pin): quotable as a preliminary estimate, NOT an address
//   no match    — keep the rider's words and reveal City + ST, so the quote still has what it needs
//                 (hasEndpoint accepts a city alone) and nothing is a dead end
//
// PREFILL: when the chat already named the place, the seed carries {name, city, state} with no label.
// On mount we resolve "<name>, <city>, <state>" once and either fill it (a confident match) or offer
// the matches — so the rider doesn't retype what they already said in chat.
export function PlaceField({ label, ariaTag, place, onSelect, onClear, onCity, onState, hint,
                             required = true, placeholder = 'Search a place or address…' }) {
  // The box shows the endpoint's CURRENT value — a resolved address, or the bare "City, ST" the chat
  // captured. Seeded once, so an empty box always means an empty endpoint: a value floating in the
  // sub-line under an empty box reads as a stale leftover (and is how a cleared field looked like it
  // still held a place).
  const [text, setText] = useState(() => place?.label || cityState(place) || '');
  const [manual, setManual] = useState(false);       // City + ST fallback revealed
  const [didYouMean, setDidYouMean] = useState([]);  // prefill found several — offer, don't guess
  const { suggestions, setSuggestions, open, setOpen, loading, boxRef,
          runSearch, resolveByName, resolvePick } = useLocationSuggest();
  const prefilled = useRef(false);

  const status = locationStatus(place);
  const entered = !!(place?.label || place?.city);
  const isResolved = status === 'resolved';
  const isApprox = status === 'approximate' || (entered && !!place?.label && !isSpecificEnough(place));
  // Sub-line under the box: the venue name (when the box is showing its street address) plus City, ST.
  const venue = (place?.name || '').trim();
  const showVenue = venue && venue !== (place?.label || '').trim();

  // Mirror an EXTERNALLY-resolved value into the box (the prefill, or a pick). Only a real place
  // carries a non-empty label — syncing on every render would wipe whatever the rider is mid-way
  // through typing (typing clears the place, which would otherwise bounce back as setText('')).
  useEffect(() => {
    if (place?.label) setText(place.label);
  }, [place]);

  // Resolve the chat-named place ONCE per mount. CustomerApp remounts the form with a fresh key when
  // the seed changes, so a new itinerary re-resolves while re-renders don't.
  useEffect(() => {
    if (prefilled.current) return;
    const name = (place?.name || '').trim();
    if (!name || place?.label) return;                    // nothing to resolve, or already resolved
    if (Array.isArray(place?.candidates) && place.candidates.length) return;  // server picker wins
    prefilled.current = true;
    let cancelled = false;
    (async () => {
      const rows = await resolveByName([name, place?.city, place?.state].filter(Boolean).join(', '));
      if (cancelled || !rows.length) return;
      // Confidence is the TOP ROW'S NAME, not the row count. Autocomplete nearly always returns
      // several rows even for an unmistakable place — "Bradley University, Peoria, IL" also returns
      // its Student Health Services, Tennis Courts and Baseball Fields — so requiring a single row
      // would mean never prefilling anything. A top row whose name IS what the rider said is the
      // place they meant; anything else ("Hilton" -> "Embassy Suites by Hilton East Peoria…") is a
      // genuine choice we must not make for them.
      //
      // We also only auto-fill when the chat gave a city or state to check the answer against. With
      // no geography there is nothing to validate against: "Toronto" resolves to Toronto, OHIO — the
      // US restriction doing its job, but a rider who meant Canada would never see that it had been
      // applied for them. Offering the rows puts "OH, USA" in front of them instead. Checked BEFORE
      // the Details call, so an unverifiable match costs nothing.
      const canVerify = !!(place?.city || place?.state);
      if (!canVerify || !sameName(rows[0].mainText, name)) { setDidYouMean(rows); return; }
      const resolved = await resolvePick(rows[0].placeId);
      if (cancelled) return;
      const p = resolved ? toPlace(resolved) : null;
      if (p && agreesWithSeed(p, place)) onSelect({ ...p, name: p.name || name });
      else setDidYouMean(rows);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const next = e.target.value;
    setText(next);
    setDidYouMean([]);
    // Typing over ANY value the box is showing unlocks the endpoint — that's what replaces a "change"
    // button. It covers the coarse "City, ST" case too, so the box and the endpoint never disagree:
    // if the box no longer reads "Naperville, IL", the endpoint is no longer Naperville.
    if (place?.label || place?.city) onClear();
    runSearch(next);
  };

  const pick = async (s) => {
    const shown = s.fullText || s.mainText || '';
    setText(shown);
    setSuggestions([]);
    setDidYouMean([]);
    setOpen(false);
    const resolved = await resolvePick(s.placeId);
    if (resolved) {
      const p = toPlace(resolved);
      onSelect({ ...p, name: p.name || s.mainText || '' });
      setManual(false);
      return;
    }
    // Details failed — keep what they chose rather than dropping it, and let them add the city.
    onSelect({ label: shown, name: s.mainText || '', city: place?.city || '', state: place?.state || '',
               lat: null, lng: null, isAirport: false, iataCode: null, precision: null, hasCoords: false });
    setManual(true);
  };

  // No match: keep the rider's exact words and reveal City + ST, so a place Google doesn't know is
  // never a dead end (hasEndpoint is satisfied by a city alone).
  //
  // The typed text becomes the LABEL, not just the name. Everything downstream reads `label`:
  // tokenPlace sends it as the address, placePhrase builds the confirmed sentence from it, and
  // RouteCard renders it — so parking it only in `name` silently dropped the rider's own words from
  // the itinerary they were shown. `name` mirrors it so nothing re-renders it as a separate venue.
  const useAsTyped = () => {
    const t = text.trim();
    if (!t) return;
    setSuggestions([]);
    setOpen(false);
    setManual(true);
    onSelect({ label: t, name: t, city: place?.city || '', state: place?.state || '',
               lat: null, lng: null, isAirport: false, iataCode: null, precision: null, hasCoords: false });
  };

  return (
    <div className="space-y-1.5" ref={boxRef}>
      {/* Asterisk inlined rather than importing RouteFormMulti's <Req/> — that module imports from this
          one, so pulling it back in here would close an import cycle. */}
      <span className="block text-10 font-semibold uppercase tracking-brand text-peoria-muted">
        {label}{required && <span className="text-danger" aria-hidden="true"> *</span>}
      </span>

      <div className="relative">
        <input
          className={cx(INPUT_BASE,
            isResolved ? 'border-green-500/60 bg-green-500/10'
              : isApprox ? 'border-amber-500/60 bg-amber-500/10'
              : 'border-peoria-border focus:border-peoria-red')}
          placeholder={placeholder}
          aria-label={`${ariaTag} location`}
          value={text}
          onChange={onChange}
          onFocus={() => suggestions.length && setOpen(true)}
        />
        {isResolved && !loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-700 text-sm">✓</span>
        )}
        {isApprox && !loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-700 text-sm font-semibold">≈</span>
        )}
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-peoria-muted text-11">…</span>
        )}
        {open && suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-card border border-peoria-border bg-peoria-surface2 shadow-pop">
            {suggestions.map((s, i) => (
              <li key={s.placeId || i}>
                <button type="button" onClick={() => pick(s)}
                  className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-peoria-surface focus:bg-peoria-surface focus:outline-none">
                  <span className="text-13 font-medium text-peoria-text">{s.mainText}</span>
                  {s.secondaryText && <span className="text-11 text-peoria-muted">{s.secondaryText}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && !loading && suggestions.length === 0 && text.trim().length >= 3 && (
          <div className="absolute z-10 mt-1 w-full rounded-card border border-peoria-border bg-peoria-surface2 px-3 py-2 shadow-pop">
            <div className="text-11 text-peoria-muted mb-1">No match from search — you can enter it manually.</div>
            <button type="button" onClick={useAsTyped}
              className="text-13 font-medium text-peoria-red underline hover:opacity-80">
              Use “{text.trim()}” as-is
            </button>
          </div>
        )}
      </div>

      {/* One quiet line under the box. The box itself always shows the endpoint's value (a resolved
          address, or the coarse "City, ST"), so this line only ever ADDS information — it never
          carries a value the box doesn't show. An empty box therefore has no line at all: a location
          floating under an empty box reads as a stale leftover. */}
      {isApprox ? (
        <div className="text-11 text-amber-700">
          {hint || 'City only — enough for an estimate; type above to pin the exact address.'}
        </div>
      ) : isResolved ? (
        <div className="text-11 text-peoria-muted">
          {place?.isAirport ? '✈ ' : ''}
          {showVenue ? venue : ''}
          {showVenue && cityState(place) ? ' · ' : ''}
          {cityState(place)}
          {place?.iataCode ? ` · ${place.iataCode}` : ''}
        </div>
      ) : null}

      {/* Prefill found several places with this name — offer them instead of guessing one. */}
      {didYouMean.length > 0 && (
        <div className="rounded-card border border-peoria-red/40 bg-peoria-surface2">
          <div className="flex items-center justify-between px-3 pt-2 pb-1">
            <span className="text-11 font-medium text-peoria-muted">
              We found a few places with that name — which one?
            </span>
            <button type="button" onClick={() => setDidYouMean([])}
              className="text-11 text-peoria-muted hover:text-peoria-text">dismiss</button>
          </div>
          <ul className="max-h-56 overflow-auto">
            {didYouMean.map((s, i) => (
              <li key={s.placeId || i}>
                <button type="button" onClick={() => pick(s)}
                  className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-peoria-surface focus:bg-peoria-surface focus:outline-none">
                  <span className="text-13 font-medium text-peoria-text">{s.mainText}</span>
                  {s.secondaryText && <span className="text-11 text-peoria-muted">{s.secondaryText}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* City + ST escape hatch — revealed only when search couldn't place them, since the quote needs
          at least a city. Stays visible afterwards so they can correct it. */}
      {(manual || (entered && !place?.label && !place?.city)) && (
        <div className="grid grid-cols-[1fr_5rem] gap-2">
          <input className={INPUT_BASE} placeholder="City" aria-label={`${ariaTag} city`}
            value={place?.city || ''} onChange={(e) => onCity(e.target.value)} />
          <input className={cx(INPUT_BASE, 'uppercase', stateInvalid(place?.state) && 'border-danger')}
            placeholder="ST" maxLength={2} aria-label={`${ariaTag} state`}
            value={place?.state || ''} onChange={(e) => onState(e.target.value)} />
        </div>
      )}
      {manual && stateInvalid(place?.state) && (
        <div className="text-11 text-danger">Use a 2-letter US state code (e.g. IL).</div>
      )}
    </div>
  );
}

export default function RoutePopup({ isOpen, onClose, onConfirm, initial }) {
  const [seed] = useState(() => seedOf(initial));
  const [from, setFrom] = useState(seed.from || null);
  const [to, setTo] = useState(seed.to || null);
  const [stops, setStops] = useState(seed.stops || []);

  // Require the pickup and destination to resolve to a specific place (street address / venue /
  // airport), not a bare city — otherwise the quote gets a vague origin and the agent has to ask for
  // the street address anyway. Stops stay lenient (a town-level rest stop is fine).
  const fromOk = isSpecificEnough(from);
  const toOk = isSpecificEnough(to);
  const canConfirm = !!from && !!to && fromOk && toOk;

  const addStop = () => setStops((s) => [...s, null]);
  const setStopAt = (i, place) => setStops((s) => s.map((v, idx) => (idx === i ? place : v)));
  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));

  const confirm = () => {
    if (!canConfirm) return;
    // Drop unpicked slots AND any duplicate stop so a repeated stop never reaches the agents.
    const verifiedStops = dedupeStops(stops.filter(Boolean));
    let routeText = `My charter route — pickup from ${placePhrase(from)}; destination ${placePhrase(to)}`;
    if (verifiedStops.length) {
      routeText += `; with ${verifiedStops.length === 1 ? 'a stop' : 'stops'} at ${verifiedStops.map(placePhrase).join('; then ')}`;
    }
    routeText += '.';
    onConfirm(routeText, { from, to, stops: verifiedStops });
  };

  const footer = (
    <>
      <Button variant="peoriaOutline" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="peoria" onClick={confirm} disabled={!canConfirm}>
        Confirm route
      </Button>
    </>
  );

  return (
    <Modal open={isOpen} onClose={onClose} title="Set your route" size="md" footer={footer} tone="peoria">
      <div>
        {/* Verified location fields, ordered as the trip runs — Departure, then any Stops along the
            way, then Arrival. Each resolves via the backend geocoder (no map). */}
        <div className="space-y-4">
          <div>
            <div className="mb-1.5 text-11 font-semibold uppercase tracking-wide text-peoria-muted">Departure</div>
            <LocationField
              label="From"
              placeholder="Pickup — street address or venue"
              value={from}
              onSelect={setFrom}
              onClear={() => setFrom(null)}
              invalid={!!from && !fromOk}
              hint="Pick a specific street address or venue — a city on its own isn't precise enough for the pickup."
            />
          </div>

          <div>
            <div className="mb-1.5 text-11 font-semibold uppercase tracking-wide text-peoria-muted">Stops along the way</div>
            <div className="space-y-3">
              {stops.map((stop, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="flex-1">
                    <LocationField
                      label={`Stop ${i + 1}`}
                      placeholder="A stop along the way"
                      value={stop}
                      onSelect={(p) => setStopAt(i, p)}
                      onClear={() => setStopAt(i, null)}
                    />
                    {duplicateStopIndices(stops).has(i) && (
                      <div className="mt-1 text-11 text-danger">Duplicate stop — it won't be added twice.</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStop(i)}
                    className="mt-7 rounded-btn p-1 text-peoria-muted transition-colors hover:bg-peoria-surface2 hover:text-danger"
                    title="Remove stop"
                    aria-label={`Remove stop ${i + 1}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addStop}
              className="mt-2 inline-flex items-center gap-1 text-13 font-medium text-peoria-red hover:underline"
            >
              <Plus size={14} /> Add stop
            </button>
          </div>

          <div>
            <div className="mb-1.5 text-11 font-semibold uppercase tracking-wide text-peoria-muted">Arrival</div>
            <LocationField
              label="To"
              placeholder="Destination — venue, address, or airport"
              value={to}
              onSelect={setTo}
              onClear={() => setTo(null)}
              invalid={!!to && !toOk}
              hint="Pick a specific venue, address, or airport — a city on its own isn't specific enough."
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
